import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { DEFAULT_JOURNEY_CONFIG, type JourneyConfiguration } from '@/types';
import { useAuth } from '@/contexts/customer-auth-context';
import { fetchAccountConfiguration, fetchPreAuthConfiguration } from '@/lib/config-api';

interface ConfigurationContextValue {
  config: JourneyConfiguration;
  isPreviewMode: boolean;
  isDemoMode: boolean;
  isLoading: boolean;
  useMockData: boolean;
  /**
   * Load account-specific configuration and return it.
   * This is used during login to determine navigation before the useEffect runs.
   * Returns the merged config (account config + pre-auth config).
   */
  loadAccountConfigAndReturn: (accountId: string) => Promise<JourneyConfiguration>;
}

const ConfigurationContext = createContext<ConfigurationContextValue | undefined>(undefined);

interface ConfigurationProviderProps {
  children: ReactNode;
}

// Helper to get initial config from URL hash or sessionStorage synchronously
// Priority: URL hash (immediate) > sessionStorage (persisted) > defaults
function getInitialConfig(): JourneyConfiguration {
  // First, try to get config from URL hash (passed by Hub for immediate availability)
  try {
    const hash = window.location.hash;
    if (hash.startsWith('#config=')) {
      const configBase64 = hash.slice('#config='.length);
      const configJson = decodeURIComponent(atob(configBase64));
      const config = JSON.parse(configJson) as JourneyConfiguration;
      // Store in sessionStorage for persistence across navigation
      sessionStorage.setItem('hub_preview_config', configJson);
      // Clear hash to keep URL clean (optional, but prevents re-parsing on navigation)
      if (window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      return mergeConfigWithDefaults(config);
    }
  } catch {
    // Failed to parse config from URL hash
  }

  // Fall back to sessionStorage (for navigation within preview)
  try {
    const storedConfig = sessionStorage.getItem('hub_preview_config');
    if (storedConfig) {
      return mergeConfigWithDefaults(JSON.parse(storedConfig) as JourneyConfiguration);
    }
  } catch {
    // Failed to load config from sessionStorage
  }
  return DEFAULT_JOURNEY_CONFIG;
}

type Providers = JourneyConfiguration['payment']['providers'];

// The compiled API config may omit `providers` or any single method (the Python
// type defaults every one to None), so merge method by method rather than by spread.
function mergeProviders(base: Providers, incoming: Providers | null | undefined): Providers {
  const next: Partial<Providers> = incoming ?? {};
  return {
    card: { ...base.card, ...next.card },
    sepa: { ...base.sepa, ...next.sepa },
    multibanco: { ...base.multibanco, ...next.multibanco },
    mb_way: { ...base.mb_way, ...next.mb_way },
    bacs: { ...base.bacs, ...next.bacs },
  };
}

// Helper to deep merge fetched config with defaults
export function mergeConfigWithDefaults(fetchedConfig: JourneyConfiguration): JourneyConfiguration {
  return {
    payment: {
      ...DEFAULT_JOURNEY_CONFIG.payment,
      ...fetchedConfig.payment,
      providers: mergeProviders(DEFAULT_JOURNEY_CONFIG.payment.providers, fetchedConfig.payment?.providers),
      paymentPlan: fetchedConfig.payment?.paymentPlan || DEFAULT_JOURNEY_CONFIG.payment.paymentPlan,
    },
    ui: {
      ...DEFAULT_JOURNEY_CONFIG.ui,
      ...fetchedConfig.ui,
      branding: {
        ...DEFAULT_JOURNEY_CONFIG.ui.branding,
        ...fetchedConfig.ui?.branding,
        colors: {
          ...DEFAULT_JOURNEY_CONFIG.ui.branding.colors,
          ...fetchedConfig.ui?.branding?.colors,
        },
      },
      features: {
        ...DEFAULT_JOURNEY_CONFIG.ui.features,
        ...fetchedConfig.ui?.features,
      },
      termsLinks: fetchedConfig.ui?.termsLinks || DEFAULT_JOURNEY_CONFIG.ui.termsLinks,
    },
    authentication: {
      ...DEFAULT_JOURNEY_CONFIG.authentication,
      ...fetchedConfig.authentication,
    },
  };
}

export function ConfigurationProvider({ children }: ConfigurationProviderProps) {
  // Check if we're in preview/demo mode first (needed for initial config)
  // Store in sessionStorage to persist across navigation and re-renders
  const getInitialPreviewMode = (): boolean => {
    // Check sessionStorage first (persists across navigation)
    try {
      const stored = sessionStorage.getItem('hub_preview_mode');
      if (stored === 'true') return true;
    } catch (e) {
      // Ignore errors
    }
    // Fallback to URL params (for initial load)
    const urlPreview = new URLSearchParams(window.location.search).get('preview') === 'true';
    if (urlPreview) {
      try {
        sessionStorage.setItem('hub_preview_mode', 'true');
      } catch (e) {
        // Ignore errors
      }
    }
    return urlPreview;
  };

  const getInitialDemoMode = (): boolean => {
    // Check sessionStorage first
    try {
      const stored = sessionStorage.getItem('hub_demo_mode');
      if (stored === 'true') return true;
    } catch (e) {
      // Ignore errors
    }
    // Fallback to URL params
    const urlDemoParam = new URLSearchParams(window.location.search).get('demo') === 'true';
    const isInIframe = window.self !== window.top;
    const isFromHub =
      isInIframe ||
      document.referrer.includes('hub.localhost') ||
      document.referrer.includes('localhost') ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === 'connect.localhost';
    const demoMode = urlDemoParam && isFromHub;
    if (demoMode) {
      try {
        sessionStorage.setItem('hub_demo_mode', 'true');
      } catch (e) {
        // Ignore errors
      }
    }
    return demoMode;
  };

  const [isPreviewMode] = useState(() => getInitialPreviewMode());
  const [isDemoMode] = useState(() => getInitialDemoMode());

  // Get account ID from auth context (needed to fetch configuration)
  const { accountId, getAccessToken } = useAuth();

  // Track if we've already loaded the pre-auth config
  const [preAuthConfigLoaded, setPreAuthConfigLoaded] = useState(false);
  
  // Store the pre-auth config so we can reset to it on logout
  const [preAuthConfig, setPreAuthConfig] = useState<JourneyConfiguration | null>(null);

  // Initialize config from URL hash or sessionStorage synchronously in preview/demo mode
  // This prevents race conditions where config arrives after login decisions are made
  const [config, setConfig] = useState<JourneyConfiguration>(() => {
    if (isPreviewMode || isDemoMode) {
      return getInitialConfig();
    }
    return DEFAULT_JOURNEY_CONFIG;
  });

  // Check if we got config from URL hash or sessionStorage (i.e., not just defaults)
  const hasInitialConfig = (): boolean => {
    // Check URL hash first
    if (window.location.hash.startsWith('#config=')) {
      return true;
    }
    // Check sessionStorage
    try {
      return !!sessionStorage.getItem('hub_preview_config');
    } catch {
      return false;
    }
  };

  // Start with loading=true to wait for configs before rendering
  // In preview/demo mode with URL hash config, we can start immediately (no waiting)
  // Otherwise, wait for config to load (pre-auth fetch or postMessage)
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if ((isPreviewMode || isDemoMode) && hasInitialConfig()) {
      return false; // Config available immediately from URL hash or sessionStorage
    }
    return true; // Wait for config to load
  });

  const useMockData = isPreviewMode || isDemoMode; // In preview or demo mode, use mock data

  // Apply theme to document root (only after config is loaded)
  useEffect(() => {
    if (isLoading) return; // Don't apply theme until config is loaded
    const theme = config.ui.branding.theme;
    document.documentElement.setAttribute('data-theme', theme);
    // Show the body now that config is loaded and theme is applied
    // Use requestAnimationFrame to ensure theme CSS is applied before showing
    requestAnimationFrame(() => {
      document.body.classList.add('config-loaded');
    });
  }, [config.ui.branding.theme, isLoading]);

  // Apply brand colors to CSS variables (only after config is loaded)
  useEffect(() => {
    if (isLoading) return; // Don't apply colors until config is loaded
    const { branding } = config.ui;

    // Helper function to detect current color scheme
    const getIsDarkMode = (): boolean => {
      return document.documentElement.classList.contains('dark') || window.matchMedia('(prefers-color-scheme: dark)').matches;
    };

    // Helper function to calculate foreground color if not provided
    const getForegroundColor = (bgColor: string): string => {
      // Simple luminance calculation to determine if text should be light or dark
      const hex = bgColor.replace('#', '');
      const r = Number.parseInt(hex.substring(0, 2), 16);
      const g = Number.parseInt(hex.substring(2, 4), 16);
      const b = Number.parseInt(hex.substring(4, 6), 16);

      // Calculate relative luminance (simplified)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      // Return white for dark backgrounds, dark for light backgrounds
      return luminance > 0.5 ? '#312826' : '#ffffff';
    };

    // Function to apply colors based on current dark mode state
    const applyColors = () => {
      const isDarkMode = getIsDarkMode();

      // Always set --brand-base from the brand color (for brand shades --brand-1 through --brand-5)
      // This keeps the brand color system intact
      if (branding.colors.primary) {
        document.documentElement.style.setProperty('--brand-base', branding.colors.primary);
      }

      // Apply button background color to --primary (overrides CSS default of var(--brand-500))
      if (branding.colors.buttonBackground) {
        const buttonBg = isDarkMode
          ? branding.colors.buttonBackground.dark || branding.colors.buttonBackground.light
          : branding.colors.buttonBackground.light || branding.colors.buttonBackground.dark;

        if (buttonBg) {
          // Set --primary directly from buttonBackground
          // This overrides the CSS default: --primary: var(--brand-500)
          document.documentElement.style.setProperty('--primary', buttonBg);
        } else {
          // If no buttonBackground, remove the override to use CSS default
          document.documentElement.style.removeProperty('--primary');
        }
      } else {
        // If no buttonBackground configured, remove override to use CSS default
        document.documentElement.style.removeProperty('--primary');
      }

      // Apply button text color to --primary-foreground
      if (branding.colors.buttonText) {
        const buttonText = isDarkMode
          ? branding.colors.buttonText.dark || branding.colors.buttonText.light
          : branding.colors.buttonText.light || branding.colors.buttonText.dark;

        if (buttonText) {
          document.documentElement.style.setProperty('--primary-foreground', buttonText);
        } else {
          // If no buttonText, remove override to use CSS default
          document.documentElement.style.removeProperty('--primary-foreground');
        }
      } else if (branding.colors.primary) {
        // Fallback: calculate foreground color from primary if buttonText is not set
        const foregroundColor = branding.colors.primaryForeground || getForegroundColor(branding.colors.primary);
        document.documentElement.style.setProperty('--primary-foreground', foregroundColor);
      } else {
        // If no buttonText and no primary, remove override to use CSS default
        document.documentElement.style.removeProperty('--primary-foreground');
      }
    };

    // Apply colors initially
    applyColors();

    // Listen for dark mode class changes
    const observer = new MutationObserver(() => {
      applyColors();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = () => {
      applyColors();
    };
    mediaQuery.addEventListener('change', handleMediaChange);

    // Apply logo
    if (branding.logo?.url) {
      // Store logo URL in a CSS variable for use in components
      document.documentElement.style.setProperty('--brand-logo-url', `url(${branding.logo.url})`);
    }

    // Apply favicon
    if (branding.favicon?.url) {
      const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (faviconLink) {
        faviconLink.href = branding.favicon.url;
      } else {
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = branding.favicon.url;
        document.head.appendChild(newFavicon);
      }
    }

    // Apply document title from company name (sourced from content config)
    if (config.ui.companyName) {
      document.title = `${config.ui.companyName} | Customer Portal`;
    } else {
      document.title = 'Customer Portal';
    }

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, [config.ui.branding, config.ui.companyName, isLoading]);

  // Ensure preview/demo mode flags are persisted in sessionStorage
  useEffect(() => {
    try {
      if (isPreviewMode) {
        sessionStorage.setItem('hub_preview_mode', 'true');
      }
      if (isDemoMode) {
        sessionStorage.setItem('hub_demo_mode', 'true');
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }, [isPreviewMode, isDemoMode]);

  // Load pre-auth configuration from environment variables (client/portfolio scope)
  // This runs once on mount to show correct branding before login
  useEffect(() => {
    if (isPreviewMode || isDemoMode || preAuthConfigLoaded) {
      return;
    }

    let cancelled = false;

    async function loadPreAuthConfig() {
      setIsLoading(true);
      try {

        // Add timeout to prevent CI/slow networks from hanging indefinitely
        // If config takes longer than 5 seconds, proceed with defaults
        const CONFIG_TIMEOUT_MS = 5000;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let didTimeout = false;

        const timeoutPromise = new Promise<null>((resolve) => {
          timeoutId = setTimeout(() => {
            didTimeout = true;
            resolve(null);
          }, CONFIG_TIMEOUT_MS);
        });

        const fetchedConfig = await Promise.race([fetchPreAuthConfiguration(), timeoutPromise]);

        // Clear timeout if fetch won the race
        if (timeoutId && !didTimeout) {
          clearTimeout(timeoutId);
        }

        if (cancelled) return;

        if (fetchedConfig) {
          const mergedConfig = mergeConfigWithDefaults(fetchedConfig);
          setConfig(mergedConfig);
          setPreAuthConfig(mergedConfig); // Store for logout reset
        } else {
          // No pre-auth configuration found, use defaults
          setConfig(DEFAULT_JOURNEY_CONFIG);
          setPreAuthConfig(null); // No pre-auth config, will use defaults on logout
        }
      } catch {
        setPreAuthConfig(null); // On error, use defaults on logout
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setPreAuthConfigLoaded(true);
        }
      }
    }

    loadPreAuthConfig();

    return () => {
      cancelled = true;
    };
  }, [isPreviewMode, isDemoMode, preAuthConfigLoaded]);

  // Load account-specific configuration after login
  // This may include account-level overrides on top of the client/portfolio config
  useEffect(() => {
    if (isPreviewMode || isDemoMode) {
      // Preview/Demo mode: Listen for PostMessage config updates
      const handleMessage = (event: MessageEvent) => {
        // Derive expected hub URL from current location (connect.* → hub.*)
        const getExpectedHubUrl = (): string => {
          const { protocol, host } = window.location;
          // Replace 'connect.' prefix with 'hub.' prefix
          const hubHost = host.replace(/^connect\./, 'hub.');
          return `${protocol}//${hubHost}`;
        };

        // Validate origin - allow localhost (development) and hub domain (production)
        const expectedHubUrl = getExpectedHubUrl();
        const isLocalhost =
          event.origin.startsWith('http://localhost:') ||
          event.origin.startsWith('https://localhost') ||
          event.origin.startsWith('http://127.0.0.1:') ||
          event.origin.startsWith('https://127.0.0.1:') ||
          event.origin.startsWith('https://hub.localhost');

        const isHubDomain = event.origin === expectedHubUrl || event.origin.startsWith(`${expectedHubUrl}/`);

        if (!isLocalhost && !isHubDomain) {
          return;
        }

        // Handle context update messages (operator/portfolio/client info)
        if (event.data?.type === 'CONTEXT_UPDATE' && event.data?.payload) {
          const context = event.data.payload as { operatorName: string; portfolioName?: string; clientName?: string };
          try {
            sessionStorage.setItem('hub_preview_context', JSON.stringify(context));
          } catch {
            // Ignore sessionStorage errors
          }
        }

        // Handle config update messages
        if (event.data?.type === 'CONFIG_UPDATE' && event.data?.payload) {
          const payload = event.data.payload as JourneyConfiguration;

          // Store in sessionStorage for persistence
          try {
            sessionStorage.setItem('hub_preview_config', JSON.stringify(payload));
          } catch {
            // Ignore sessionStorage errors
          }

          // Deep merge: preserve existing config and update with new values
          setConfig((prevConfig) => ({
            ...prevConfig,
            payment: {
              ...prevConfig.payment,
              ...payload.payment,
              providers: mergeProviders(prevConfig.payment.providers, payload.payment?.providers),
              paymentPlan: payload.payment?.paymentPlan || prevConfig.payment.paymentPlan,
            },
            ui: {
              ...prevConfig.ui,
              ...payload.ui,
              branding: {
                ...prevConfig.ui.branding,
                ...payload.ui?.branding,
                logo: payload.ui?.branding?.logo || prevConfig.ui.branding.logo,
                logoIcon: payload.ui?.branding?.logoIcon || prevConfig.ui.branding.logoIcon,
                colors: {
                  ...prevConfig.ui.branding.colors,
                  ...payload.ui?.branding?.colors,
                },
              },
              features: {
                ...prevConfig.ui.features,
                ...payload.ui?.features,
              },
              termsLinks: payload.ui?.termsLinks || prevConfig.ui.termsLinks,
            },
            authentication: {
              ...prevConfig.authentication,
              ...payload.authentication,
            },
          }));

          // Config received from Hub - ready to render
          setIsLoading(false);
        }
      };

      window.addEventListener('message', handleMessage);

      // Send ready signal to parent (use '*' for localhost development)
      window.parent.postMessage({ type: 'PREVIEW_READY' }, '*');

      // Timeout fallback: if no config received within 3 seconds, proceed with defaults
      // This prevents the preview from being stuck if Hub fails to send config
      const timeoutId = setTimeout(() => {
        setIsLoading(false);
      }, 3000);

      return () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutId);
      };
    }

    // Normal mode: Fetch account-specific configuration after login
    if (!isPreviewMode && !isDemoMode && accountId) {
      let cancelled = false;

      async function loadAccountConfiguration() {
        // NOTE: Do NOT set isLoading(true) here!
        // Setting isLoading causes App.tsx to render a fullscreen loader which
        // unmounts the Routes tree. If this happens right after login, the navigation
        // from Login.tsx to /dashboard gets lost because the Routes tree is unmounted.
        // The app should remain functional with pre-auth config while account config loads.
        try {
          const fetchedConfig = await fetchAccountConfiguration(accountId as string, getAccessToken);

          if (cancelled) return;

          if (fetchedConfig) {
            // Merge account config with pre-auth config according to strategy:
            // - Journey (payment): Deep merge (account overrides pre-auth fields)
            // - Theme (ui.branding): Replace if account theme exists, otherwise keep pre-auth
            // - Content (ui.termsLinks, etc): Replace if account content exists, otherwise keep pre-auth
            const baseConfig = preAuthConfig || DEFAULT_JOURNEY_CONFIG;
            
            // Merge payment config (journey domain) - account overrides pre-auth
            const mergedPayment = {
              ...baseConfig.payment,
              ...fetchedConfig.payment,
              providers: mergeProviders(baseConfig.payment.providers, fetchedConfig.payment?.providers),
              // Deep merge paymentPlan - account overrides pre-auth
              paymentPlan: fetchedConfig.payment?.paymentPlan || baseConfig.payment.paymentPlan,
            };

            // For UI config (theme + content domains):
            // If account has UI config, use it (replaces pre-auth theme/content)
            // Otherwise keep pre-auth UI config
            const mergedUI = fetchedConfig.ui
              ? {
                  ...baseConfig.ui,
                  ...fetchedConfig.ui,
                  // Deep merge branding (theme domain)
                  branding: {
                    ...baseConfig.ui.branding,
                    ...fetchedConfig.ui.branding,
                    colors: {
                      ...baseConfig.ui.branding.colors,
                      ...fetchedConfig.ui.branding?.colors,
                    },
                  },
                  // Deep merge features
                  features: {
                    ...baseConfig.ui.features,
                    ...fetchedConfig.ui.features,
                  },
                  // Content domain fields (termsLinks) - account replaces pre-auth if present
                  termsLinks: fetchedConfig.ui.termsLinks || baseConfig.ui.termsLinks,
                  language: fetchedConfig.ui.language || baseConfig.ui.language,
                }
              : baseConfig.ui;

            // Merge authentication config - account overrides pre-auth
            const mergedAuth = fetchedConfig.authentication || baseConfig.authentication;

            const mergedConfig: JourneyConfiguration = {
              payment: mergedPayment,
              ui: mergedUI,
              authentication: mergedAuth,
            };

            setConfig(mergedConfig);
          } else {
            // Keep pre-auth config if no account config found
            if (preAuthConfig) {
              setConfig(preAuthConfig);
            }
          }
        } catch {
          // On error, keep pre-auth config
          if (preAuthConfig) {
            setConfig(preAuthConfig);
          }
        } finally {
          // No need to set isLoading here - we didn't set it true
          // Account config loading happens in background without blocking UI
        }
      }

      loadAccountConfiguration();

      return () => {
        cancelled = true;
      };
    }
  }, [isPreviewMode, isDemoMode, accountId, getAccessToken, preAuthConfig]);

  // Reset to pre-auth config when accountId becomes null (logout)
  useEffect(() => {
    if (isPreviewMode || isDemoMode) {
      return;
    }

    // Only reset if we've loaded pre-auth config and accountId becomes null
    if (!accountId && preAuthConfigLoaded) {
      if (preAuthConfig) {
        setConfig(preAuthConfig);
      } else {
        // No pre-auth config, reset to defaults
        setConfig(DEFAULT_JOURNEY_CONFIG);
      }
      setIsLoading(false); // Ensure loading is false after reset
    }
  }, [isPreviewMode, isDemoMode, accountId, preAuthConfigLoaded, preAuthConfig]);

  // Function to load account config and return it (for Login.tsx to await before navigating)
  // This prevents the race condition where Login navigates based on pre-auth config
  // before account-specific config (with early_verification) is loaded
  const loadAccountConfigAndReturn = async (acctId: string): Promise<JourneyConfiguration> => {
    try {
      const fetchedConfig = await fetchAccountConfiguration(acctId, getAccessToken);

      if (fetchedConfig) {
        // Merge account config with pre-auth config (same logic as useEffect)
        const baseConfig = preAuthConfig || DEFAULT_JOURNEY_CONFIG;

        const mergedPayment = {
          ...baseConfig.payment,
          ...fetchedConfig.payment,
          providers: mergeProviders(baseConfig.payment.providers, fetchedConfig.payment?.providers),
          paymentPlan: fetchedConfig.payment?.paymentPlan || baseConfig.payment.paymentPlan,
        };

        const mergedUI = fetchedConfig.ui
          ? {
              ...baseConfig.ui,
              ...fetchedConfig.ui,
              branding: {
                ...baseConfig.ui.branding,
                ...fetchedConfig.ui.branding,
                colors: {
                  ...baseConfig.ui.branding.colors,
                  ...fetchedConfig.ui.branding?.colors,
                },
              },
              features: {
                ...baseConfig.ui.features,
                ...fetchedConfig.ui.features,
              },
              termsLinks: fetchedConfig.ui.termsLinks || baseConfig.ui.termsLinks,
              language: fetchedConfig.ui.language || baseConfig.ui.language,
            }
          : baseConfig.ui;

        const mergedAuth = fetchedConfig.authentication || baseConfig.authentication;

        const mergedConfig: JourneyConfiguration = {
          payment: mergedPayment,
          ui: mergedUI,
          authentication: mergedAuth,
        };

        setConfig(mergedConfig);
        return mergedConfig;
      }
    } catch {
      // On error, return current config
    }

    // If no account config or error, return current config (pre-auth or defaults)
    return preAuthConfig || config;
  };

  return (
    <ConfigurationContext.Provider value={{ config, isPreviewMode, isDemoMode, isLoading, useMockData, loadAccountConfigAndReturn }}>
      {children}
    </ConfigurationContext.Provider>
  );
}

export function useConfiguration() {
  const context = useContext(ConfigurationContext);
  if (context === undefined) {
    throw new Error('useConfiguration must be used within a ConfigurationProvider');
  }
  return context;
}
